/**
 * Editable Field Wrapper
 * Wraps fields with inline edit controls (hover to reveal)
 */

import React, { useState } from 'react';
import { Edit2, Trash2, GripVertical } from 'lucide-react';
import type { FieldSchema } from '../../types/formSchema';

interface EditableFieldWrapperProps {
  field: FieldSchema;
  children: React.ReactNode;
  onEdit: (field: FieldSchema) => void;
  onDelete: (fieldId: string) => void;
  onMove?: (fieldId: string, direction: 'up' | 'down') => void;
  isFirst?: boolean;
  isLast?: boolean;
}

export default function EditableFieldWrapper({
  field,
  children,
  onEdit,
  onDelete,
  onMove,
  isFirst = false,
  isLast = false,
}: EditableFieldWrapperProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Field Content */}
      <div className={`relative transition-all ${
        isHovered ? 'ring-2 ring-blue-500 rounded-lg' : ''
      }`}>
        {children}

        {/* Hover Controls */}
        {isHovered && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-white shadow-lg rounded-lg border border-gray-200 p-1 z-10">
            {/* Drag Handle (if reordering enabled) */}
            {onMove && (
              <div className="flex flex-col gap-0">
                {!isFirst && (
                  <button
                    onClick={() => onMove(field.id, 'up')}
                    className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                    title="Move up"
                  >
                    <GripVertical className="w-4 h-4 rotate-180" />
                  </button>
                )}
                {!isLast && (
                  <button
                    onClick={() => onMove(field.id, 'down')}
                    className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900 transition-colors"
                    title="Move down"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {/* Edit Button */}
            <button
              onClick={() => onEdit(field)}
              className="p-2 hover:bg-blue-50 rounded text-blue-600 hover:text-blue-700 transition-colors"
              title="Edit field"
            >
              <Edit2 className="w-4 h-4" />
            </button>

            {/* Delete Button */}
            <button
              onClick={() => {
                if (window.confirm(`Delete field "${field.label}"?`)) {
                  onDelete(field.id);
                }
              }}
              className="p-2 hover:bg-red-50 rounded text-red-600 hover:text-red-700 transition-colors"
              title="Delete field"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Field Type Badge (bottom-left on hover) */}
      {isHovered && (
        <div className="absolute bottom-2 left-2 bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg z-10">
          {field.type}
        </div>
      )}
    </div>
  );
}
